import { getSchema } from "@tiptap/core";
import { StarterKit } from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { Node as PMNode, type Node, type Schema } from "@tiptap/pm/model";

// Image hosts WarEra itself trusts (its in-game "Add an Image" dialog whitelists
// these 8 registrable domains). Real <img> URLs live on CDN subdomains
// (i.imgur.com, media1.tenor.com, res.cloudinary.com, …), so we match by
// registrable-domain suffix, not exact host. `ibb.co` is imgbb's file CDN — a
// distinct registrable domain from imgbb.com, added as an alias so imgbb images
// (served from i.ibb.co) are allowed. See memory: warera-image-hosts.
export const TRUSTED_IMAGE_DOMAINS = [
  "imgur.com",
  "giphy.com",
  "tenor.com",
  "cloudinary.com",
  "postimages.org",
  "postimg.cc",
  "imgbb.com",
  "imagebam.com",
] as const;

// Suffix allowlist actually used for matching: the 8 WarEra domains + imgbb's
// file-CDN alias (ibb.co). Host is allowed when it equals one of these or is a
// subdomain of one.
const ALLOWED_IMAGE_SUFFIXES = [...TRUSTED_IMAGE_DOMAINS, "ibb.co"];

/** True when an image src points at a WarEra-trusted host (registrable-domain
 * suffix match). Non-http(s) or unparseable URLs are rejected. */
export function imageHostAllowed(src: string): boolean {
  let host: string;
  try {
    const u = new URL(src);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    host = u.host.toLowerCase();
  } catch {
    return false;
  }
  return ALLOWED_IMAGE_SUFFIXES.some((d) => host === d || host.endsWith("." + d));
}

/** Extensions shared by editor, renderer, and the worker validator. NOTE:
 * StarterKit v3 bundles Link — configure it here, never add @tiptap/extension-link
 * separately (it would log a duplicate-name warning). */
export const tiptapExtensions = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    link: {
      openOnClick: false,
      protocols: ["http", "https"],
      HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
    },
  }),
  Image.configure({
    allowBase64: false,
    HTMLAttributes: { loading: "lazy", referrerpolicy: "no-referrer" },
  }),
];

let _schema: Schema | null = null;
/** Memoized ProseMirror schema. Pure JS, no DOM — safe in the workerd runtime
 * (validated via `wrangler dev`). */
export function getDocSchema(): Schema {
  if (!_schema) _schema = getSchema(tiptapExtensions);
  return _schema;
}

// Defense-in-depth caps: bound the doc so a single field can't be used to blow
// up storage or the validator. Enforced server-side on every write.
export const MAX_NODES = 800;
export const MAX_DOC_BYTES = 96 * 1024;

export type ValidateResult = { ok: true; doc: unknown } | { ok: false; reason: string };

function httpProtocol(href: string): boolean {
  try {
    const p = new URL(href).protocol;
    return p === "http:" || p === "https:";
  } catch {
    return false;
  }
}

type JsonNode = {
  type?: string;
  attrs?: Record<string, unknown>;
  marks?: { type: string; attrs?: Record<string, unknown> }[];
  content?: JsonNode[];
};

/** Validate + sanitize a tiptap JSON doc against the schema and attribute
 * allowlists. Pure JS (no DOM) — safe in workerd. The schema is the primary
 * sanitizer: `Node.fromJSON` throws on any unknown node/mark (script, iframe,
 * style, …). Surviving links with a non-http(s) protocol and images on a
 * non-trusted host are stripped (not fatal). */
export function validateDoc(input: unknown): ValidateResult {
  // Docs travel as JSON strings end-to-end (editor state → API → DB). Coerce a
  // string to its parsed object so every layer can pass whatever it holds.
  let value = input;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return { ok: false, reason: "invalid content: not JSON" };
    }
  }
  if (JSON.stringify(value ?? null).length > MAX_DOC_BYTES) return { ok: false, reason: "content too large" };

  let node: Node;
  try {
    node = PMNode.fromJSON(getDocSchema(), value as Parameters<typeof PMNode.fromJSON>[1]);
  } catch (e) {
    return { ok: false, reason: `invalid content: ${(e as Error).message}` };
  }

  let count = 0;
  node.descendants(() => {
    count += 1;
    return true;
  });
  if (count > MAX_NODES) return { ok: false, reason: "content too large" };

  // Walk the plain JSON form (PM nodes are immutable) and strip disallowed
  // links/images.
  const clean = (n: JsonNode): JsonNode | null => {
    if (n.type === "image" && !imageHostAllowed(String(n.attrs?.src ?? ""))) return null;
    if (Array.isArray(n.marks)) {
      n.marks = n.marks.filter((m) => !(m.type === "link" && !httpProtocol(String(m.attrs?.href ?? ""))));
    }
    if (Array.isArray(n.content)) {
      n.content = n.content.map(clean).filter((c): c is JsonNode => c !== null);
    }
    return n;
  };
  return { ok: true, doc: clean(node.toJSON() as JsonNode) };
}

/** Walk a doc to plaintext (one line per top-level block). Source for OG meta,
 * search, activity-feed snippets, and length caps. Tolerant of invalid input. */
export function extractText(input: unknown): string {
  let node: Node;
  try {
    node = PMNode.fromJSON(getDocSchema(), input as Parameters<typeof PMNode.fromJSON>[1]);
  } catch {
    return "";
  }
  const lines: string[] = [];
  node.forEach((block) => lines.push(block.textContent));
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

// --- textToDoc: legacy plaintext → tiptap JSON --------------------------------
// Mirrors the candidacy page's `Linkify` exactly so migrated/imported content
// renders identically to today: markdown `[label](url)`, markdown image
// `![alt](url)`, and bare URLs become links; an image URL that resolves to a
// trusted inline source (i.imgur.com direct file or an imgur single-image page)
// becomes a block image. tiptap images are block-level, so an inline image token
// splits its surrounding text into separate paragraphs.

const LINK_RE = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+)/g;
const LEGACY_IMG_EXT = /\.(png|jpe?g|gif|webp|avif)$/i;
const IMGUR_SINGLE = /^\/([a-zA-Z0-9]+)$/; // /<id>, not /a/<id> (album) or /gallery/<id>

/** Legacy image resolution — matches Candidacy.tsx `resolveImageUrl`. Only
 * i.imgur.com direct files and imgur single-image pages become inline images;
 * everything else stays a link. (Legacy plaintext only ever inlined i.imgur.com.) */
function resolveLegacyImage(raw: string): string | null {
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  if (u.hostname === "i.imgur.com" && LEGACY_IMG_EXT.test(u.pathname)) return raw;
  if (u.hostname === "imgur.com" || u.hostname === "www.imgur.com") {
    const m = IMGUR_SINGLE.exec(u.pathname);
    if (m) return `https://i.imgur.com/${m[1]}.jpg`;
  }
  return null;
}

type TextNode = { type: "text"; text: string; marks?: { type: "link"; attrs: { href: string } }[] };
type BreakNode = { type: "hardBreak" };
type InlineNode = TextNode | BreakNode;
type ParagraphNode = { type: "paragraph"; content: InlineNode[] };
type ImageNode = { type: "image"; attrs: { src: string } };
type Block = ParagraphNode | ImageNode;

function linkText(text: string, href: string): TextNode {
  return { type: "text", text, marks: [{ type: "link", attrs: { href } }] };
}

/** Parse one paragraph (may contain single newlines → hard breaks) into blocks.
 * Image tokens flush the current paragraph and emit a standalone image block. */
function paragraphToBlocks(para: string): Block[] {
  const blocks: Block[] = [];
  let runs: InlineNode[] = [];
  const flush = () => {
    if (runs.length) blocks.push({ type: "paragraph", content: runs });
    runs = [];
  };
  const pushText = (s: string) => {
    if (s) runs.push({ type: "text", text: s });
  };

  const lines = para.split("\n");
  lines.forEach((line, li) => {
    if (li > 0) runs.push({ type: "hardBreak" });
    const re = new RegExp(LINK_RE);
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) pushText(line.slice(last, m.index));
      const imgAlt = m[1];
      const imgUrl = m[2]; // ![alt](url)
      const linkLabel = m[3];
      const linkUrl = m[4]; // [label](url)
      const bareUrl = m[5];
      const src = linkUrl ? null : resolveLegacyImage(imgUrl ?? bareUrl!);
      if (src) {
        flush();
        blocks.push({ type: "image", attrs: { src } });
      } else if (linkUrl) {
        runs.push(linkText(linkLabel ?? linkUrl, linkUrl));
      } else if (imgUrl) {
        runs.push(linkText(imgAlt || imgUrl, imgUrl)); // unresolvable image → link
      } else {
        runs.push(linkText(bareUrl!, bareUrl!));
      }
      last = m.index + m[0].length;
    }
    if (last < line.length) pushText(line.slice(last));
  });
  flush();
  return blocks;
}

/** Plaintext (with the app's markdown-link + bare-url + imgur-image conventions)
 * → tiptap JSON. Used by the one-time backfill, the scraper import path, and as a
 * defensive client render fallback for any pre-backfill row. */
export function textToDoc(text: string): unknown {
  const content: Block[] = [];
  for (const para of (text ?? "").replace(/\r\n/g, "\n").split(/\n{2,}/)) {
    if (!para.trim()) continue;
    content.push(...paragraphToBlocks(para));
  }
  if (content.length === 0) content.push({ type: "paragraph", content: [] });
  return { type: "doc", content };
}
